import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * AutoScrollBox - A wrapper component that automatically scrolls into view when rendered
 * 
 * @param {ReactNode} children - The content to render inside the box
 * @param {object} sx - Additional MUI sx props to apply to the Box
 * @param {number} delay - Delay in milliseconds before scrolling (default: 100)
 * @param {object} scrollOptions - Options for scrollIntoView behavior
 * @param {boolean} enabled - Whether auto-scroll is enabled (default: true)
 * @param {function} onScroll - Callback function called after scrolling
 */
const AutoScrollBox = ({ 
  children, 
  sx = {}, 
  delay = 100,
  scrollOptions = { behavior: 'smooth', block: 'nearest', inline: 'nearest' },
  enabled = true,
  onScroll,
  ...otherProps 
}) => {
  const boxRef = useRef(null);

  useEffect(() => {
    if (enabled && boxRef.current) {
      // Use setTimeout to ensure DOM is fully rendered
      const timeoutId = setTimeout(() => {
        if (boxRef.current) {
          boxRef.current.scrollIntoView(scrollOptions);
          
          // Call onScroll callback if provided
          if (onScroll) {
            onScroll();
          }
        }
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [enabled, delay, scrollOptions, onScroll]);

  return (
    <Box ref={boxRef} sx={sx} {...otherProps}>
      {children}
    </Box>
  );
};

export default AutoScrollBox;